import { Link } from '@mui/material';
import React, { useCallback, useMemo } from 'react';
import { useMutation } from 'react-query';

import { ordersApi, payoutOrdersApi } from 'api';
import { OrderType } from 'enums';
import { usePopupWindow, useUser } from 'hooks';
import { apiUtils } from 'utils';

type Props = {
  orderId: string;
  orderType: OrderType;
  filename: string;
};

export const ReceiptLabel: React.FC<Props> = ({
  orderId,
  orderType,
  filename,
}) => {
  const { apiRole } = useUser();

  const { openPopup, navigatePopup, closePopup } = usePopupWindow({
    fallback: (url) => apiUtils.requestDownload({ url }, filename),
  });

  const mutationFn = useMemo(
    () =>
      orderType === OrderType.Payin
        ? ordersApi.generateReceiptUrlAsRole(apiRole)
        : payoutOrdersApi.generateReceiptUrlAsRole(apiRole),
    [orderType, apiRole],
  );

  const { mutate: generateUrl, isLoading } = useMutation(mutationFn, {
    onSuccess: async ({ url }) => {
      try {
        await navigatePopup(url);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to open/download:', url, err);
      }
    },
    onError: () => {
      closePopup();
    },
  });

  const handleClick = useCallback(() => {
    openPopup();
    generateUrl({ orderId, body: { filename } });
  }, [filename, generateUrl, openPopup, orderId]);

  return (
    <Link
      component="button"
      variant="body2"
      underline="none"
      textAlign="left"
      sx={{ wordBreak: 'break-word', ...(isLoading && { opacity: 0.5 }) }}
      disabled={isLoading}
      onClick={handleClick}
    >
      {filename}
    </Link>
  );
};
